import * as React from "react"

import { cn } from "@/lib/utils"

// A tabela que não cabe na largura vira uma pilha de cartões (o CSS está em
// index.css, sob [data-cartoes]). Cada célula ganha o nome da coluna em
// data-rotulo, lido do cabeçalho. Tabela que cabe continua tabela.
// Antes valia só no celular (até 639px); no notebook a tabela larga rolava de
// lado escondendo colunas, e o Rick vetou isso em 19/10/2026 ("scroll
// horizontal é muito ruim… esconde muita coisa"): agora vale em qualquer largura.
const CELULAR = "all"

function useCartoesNoCelular(ref: React.RefObject<HTMLDivElement | null>) {
  React.useLayoutEffect(() => {
    const caixa = ref.current
    const mq = typeof window.matchMedia === "function" ? window.matchMedia(CELULAR) : null
    if (!caixa || !mq) return
    let quadro = 0
    let remedirPendente = false
    let larguraMedida = -1
    // remedir: só quando a largura muda. Mudança de linhas só refaz os rótulos
    // se a tabela já virou cartões; sem isso, um componente que se redesenha ao
    // mudar de tamanho ligaria e desligaria os cartões sem parar.
    const avaliar = (remedir = false) => {
      remedirPendente ||= remedir
      cancelAnimationFrame(quadro)
      quadro = requestAnimationFrame(() => {
        const remedirAgora = remedirPendente
        remedirPendente = false
        const tabela = caixa.querySelector(":scope > table") as HTMLTableElement | null
        if (!tabela) {
          delete caixa.dataset.cartoes
          return
        }
        const cabecalho = Array.from(
          tabela.querySelectorAll(":scope > thead > tr:last-child > th")
        ).map((th) => th.textContent?.trim() ?? "")
        for (const tr of Array.from(tabela.querySelectorAll(":scope > tbody > tr, :scope > tfoot > tr"))) {
          let coluna = 0
          for (const td of Array.from(tr.children) as HTMLTableCellElement[]) {
            // Célula que ocupa várias colunas (estado vazio, subtotal) fica sem rótulo.
            const rotulo = td.colSpan > 1 ? "" : (cabecalho[coluna] ?? "")
            if (td.getAttribute("data-rotulo") !== rotulo) td.setAttribute("data-rotulo", rotulo)
            coluna += td.colSpan || 1
          }
        }
        // A barra de endereço do celular dispara resize ao rolar sem mudar a
        // largura: remedir aí faria a página pular.
        if ("cartoes" in caixa.dataset && (!remedirAgora || caixa.clientWidth === larguraMedida)) return
        larguraMedida = caixa.clientWidth
        delete caixa.dataset.cartoes
        if (tabela.scrollWidth > caixa.clientWidth + 4) caixa.dataset.cartoes = ""
      })
    }
    const aoRedimensionar = () => avaliar(true)
    avaliar(true)
    // Atributos não entram na observação: o data-rotulo não dispara de novo.
    const observador = new MutationObserver(() => avaliar())
    observador.observe(caixa, { childList: true, subtree: true, characterData: true })
    mq.addEventListener?.("change", aoRedimensionar)
    window.addEventListener("resize", aoRedimensionar)
    return () => {
      cancelAnimationFrame(quadro)
      observador.disconnect()
      mq.removeEventListener?.("change", aoRedimensionar)
      window.removeEventListener("resize", aoRedimensionar)
    }
  }, [ref])
}

function Table({ className, ...props }: React.ComponentProps<"table">) {
  const caixa = React.useRef<HTMLDivElement>(null)
  useCartoesNoCelular(caixa)
  return (
    <div
      ref={caixa}
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
}

/**
 * Caixa para as tabelas montadas à mão (<table> direto): mesma rolagem lateral
 * da Table e os mesmos cartões no celular.
 */
function CaixaTabela({ className, ...props }: React.ComponentProps<"div">) {
  const caixa = React.useRef<HTMLDivElement>(null)
  useCartoesNoCelular(caixa)
  return <div ref={caixa} className={cn("overflow-x-auto", className)} {...props} />
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("bg-cabecalho-tabela [&_tr]:border-b dark:bg-transparent", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "data-[state=selected]:bg-muted border-b border-border/70 transition-colors",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-2 text-left align-middle text-xs font-medium text-muted-foreground whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  )
}

export {
  Table,
  CaixaTabela,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
